/**
 * ScanSettings.jsx — Admin / Manager page to configure tenant defaults for
 * scan-to-count flows (Receiving, Production Orders).
 */
import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Camera, Lightning, Stack, Package, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { loadScanSettings, saveScanSettings, DEFAULTS } from '../lib/scanSettings';

export default function ScanSettings() {
  const [submitMode, setSubmitMode] = useState(DEFAULTS.default_submit_mode);
  const [carton, setCarton] = useState(DEFAULTS.default_carton_multiplier);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadScanSettings().then((s) => {
      setSubmitMode(s.default_submit_mode);
      setCarton(s.default_carton_multiplier);
    }).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveScanSettings({
        default_submit_mode: submitMode,
        default_carton_multiplier: Math.max(1, parseInt(carton, 10) || 1),
      });
      setSubmitMode(saved.default_submit_mode);
      setCarton(saved.default_carton_multiplier);
      toast.success('Scan defaults saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div data-testid="scan-settings-page" className="p-4 md:p-6 max-w-2xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-semibold uppercase tracking-widest">
            <Camera size={14} weight="fill" /> Admin · Scan defaults
          </div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Scan-to-count defaults</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Tenant-wide defaults for Receiving and Production Order scan flows. Every user on this account
            starts with these settings; they can still override per-session.
          </p>
        </div>

        {loading ? (
          <div className="h-40 bg-muted rounded-md animate-pulse" />
        ) : (
          <div className="bg-card border border-border rounded-lg p-5 space-y-6">
            <div>
              <Label className="overline">Default submit mode</Label>
              <div className="grid grid-cols-2 gap-1 mt-2 p-1 bg-muted rounded-md">
                <button
                  type="button"
                  onClick={() => setSubmitMode('continuous')}
                  data-testid="default-submit-continuous"
                  className={`py-3 text-xs font-bold uppercase tracking-wider rounded transition flex items-center justify-center gap-2 ${submitMode === 'continuous' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <Lightning size={14} weight="fill" /> Continuous
                </button>
                <button
                  type="button"
                  onClick={() => setSubmitMode('batch')}
                  data-testid="default-submit-batch"
                  className={`py-3 text-xs font-bold uppercase tracking-wider rounded transition flex items-center justify-center gap-2 ${submitMode === 'batch' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <Stack size={14} weight="fill" /> Batch
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {submitMode === 'continuous'
                  ? 'Each scan is POSTed immediately. Best for warehouses with solid Wi-Fi.'
                  : 'Scans accumulate locally; the operator submits them in one batch. Better for patchy networks and offline workflows.'}
              </p>
            </div>

            <div>
              <Label className="overline flex items-center gap-1">
                <Package size={11} weight="fill" /> Default carton multiplier
              </Label>
              <Input
                type="number" min={1} max={9999} inputMode="numeric"
                value={carton}
                onChange={(e) => setCarton(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="mt-2 font-mono text-base"
                data-testid="default-carton-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                When &gt; 1, each scan counts as N units instead of 1. Handy for case-pack receiving
                (e.g. each outer carton = 12 inner bottles). Operators can override per-session.
              </p>
            </div>

            <div className="pt-4 border-t border-border">
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-scan-defaults-btn"
                className="w-full h-11 bg-brand hover:bg-brand/90 text-brand-foreground font-bold"
              >
                <FloppyDisk size={16} className="mr-2" weight="fill" />
                {saving ? 'Saving…' : 'Save defaults'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
